import React, { useState, useEffect } from "react";
import SubscriptionSetup from "../components/SubscriptionSetup";
import { useSubscription } from "../../shared/hooks/useSubscription";
import { getTierForSeats, tierPricing } from "../tierPricing";
import UpgradeModal from "../components/UpgradeModal";
import "../subscription.css";

const SubscriptionDashboard = () => {
	const {
		subscription,
		usage,
		loading,
		error,
		openCustomerPortal,
		usedSeats,
		refresh: fetchSubscriptionData,
		//orgId,
	} = useSubscription();

	const [selectedSeats, setSelectedSeats] = useState(subscription?.seats || 3);
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);

	useEffect(() => {
		if (subscription) {
			setSelectedSeats(subscription.seats);
		}
	}, [subscription]);

	const handleTierChange = (seats) => {
		setSelectedSeats(seats);
	};

	const tier = getTierForSeats(selectedSeats);

	const calculateCost = (seatCount) => {
		const tier = getTierForSeats(seatCount);
		return (tier.amount / 100).toFixed(2);
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "active":
				return "green";
			case "trialing":
				return "blue";
			case "past_due":
				return "orange";
			case "canceled":
				return "red";
			default:
				return "gray";
		}
	};

	const formatDate = (timestamp) => {
		if (!timestamp) return "No date available";

		// Handle Firestore Timestamp object with _seconds
		if (timestamp && timestamp._seconds) {
			const date = new Date(timestamp._seconds * 1000);
			const month = date.toLocaleString("en-US", { month: "short" });
			const day = date.getDate();
			const time = date.toLocaleString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});

			return `${month} ${day} at ${time}`;
		}

		return "Invalid Date";
	};

	if (loading) {
		return <div className="loading">Loading subscription details...</div>;
	}

	if (error) {
		return (
			<div className="error-container">
				<div className="error-message">Error: {error}</div>
				<button onClick={fetchSubscriptionData} className="retry-btn">
					Retry
				</button>
			</div>
		);
	}

	if (!subscription) {
		return <SubscriptionSetup onSuccess={fetchSubscriptionData} />;
	}

	return (
		<div className="subscription-dashboard">
			<div className="subscription-header">
				<h2>Subscription Management</h2>
				<div className={`status-badge status-${subscription.status}`}>
					<span style={{ color: getStatusColor(subscription.status) }}>
						{subscription.status.charAt(0).toUpperCase() +
							subscription.status.slice(1)}
					</span>
				</div>
			</div>

			<div className="subscription-details">
				<div className="detail-card">
					<h3>Current Plan</h3>
					<div className="plan-info">
						<div className="seats-info">
							<span className="seats-used">{usage?.usedSeats}</span>
							<span className="seats-separator">/</span>
							<span className="seats-total">{subscription.seats}</span>
							<span className="seats-label">seats used</span>
						</div>
						<div className="plan-cost">
							${calculateCost(subscription.seats)}/month
						</div>
					</div>
				</div>

				<div className="detail-card">
					<h3>Billing Period</h3>
					<div className="billing-period">
						<div>Start: {formatDate(subscription?.lastPaymentDate)}</div>
						<div>End: {formatDate(subscription?.current_period_end)}</div>
					</div>
				</div>
			</div>

			<div className="seat-management">
				<h3>Manage Seats</h3>

				<div className="seat-selector">
					<div className="tier-boxes">
						{tierPricing.map((option) => (
							<button
								key={option.maxSeats}
								className={`tier-box ${
									selectedSeats === option.maxSeats ? "active" : ""
								}`}
								onClick={() => handleTierChange(option.maxSeats)}
							>
								<div className="tier-box-label">{option.label}</div>
								<div className="tier-box-price">
									${(option.amount / option.maxSeats / 100).toFixed(2)} / seat
								</div>
							</button>
						))}
					</div>

					<div className="pricing-summary">
						<strong>Total: ${(tier.amount / 100).toFixed(2)}/month</strong>
					</div>
					<button
						onClick={() => setShowUpgradeModal(true)}
						disabled={(selectedSeats === subscription.seats) || (selectedSeats < subscription.seats && usage?.usedSeats > selectedSeats)}
						className="upgrade-btn"
					>
						{selectedSeats < subscription.seats
							? "Downgrade Plan"
							: "Upgrade Plan"}
					</button>
				</div>
			</div>

			<div className="subscription-actions">
				<button onClick={openCustomerPortal} className="portal-btn">
					Manage Billing & Payment Methods
				</button>
			</div>

			{usedSeats >= subscription.seats && (
				<div className="warning-banner">
					<strong>Seat limit reached!</strong> You're using all{" "}
					{subscription.seats} seats. Upgrade to add more team members.
				</div>
			)}
			{showUpgradeModal && (
				<UpgradeModal
					currentSeats={subscription.seats}
					newSeats={selectedSeats}
					orgId={subscription.orgId}
					onClose={() => setShowUpgradeModal(false)}
					onUpgrade={fetchSubscriptionData}
					usedSeats={usage?.usedSeats}
				/>
			)}
		</div>
	);
};

export default SubscriptionDashboard;
